import type { LodTreeNode } from "@faro-lotv/lotv";

/**
 * @param options Bounded work producers; run must observe the supplied job signal.
 * @returns Completion after results have been committed in dispatch order.
 */
export async function runClashQueue<Item, Result>(options: {
  next(): Item | undefined;
  run(item: Item, slot: number): Promise<Result>;
  commit(item: Item, result: Result): Promise<void>;
  concurrency: number;
  control: ClashJobControl;
  signal: AbortSignal;
  weight?(item: Item): number;
  maxWeight?: number;
}): Promise<void> {
  const {
    next,
    run,
    commit,
    control,
    signal,
    weight = () => 1,
    maxWeight = Infinity,
  } = options;
  if (!Number.isInteger(options.concurrency) || options.concurrency < 1) {
    throw new Error("Invalid clash concurrency");
  }
  const slots = Array.from(
    { length: options.concurrency },
    (_, index) => index,
  );
  const pending: Array<{
    item: Item;
    slot: number;
    weight: number;
    result: Promise<{ value: Result } | { error: unknown }>;
  }> = [];
  let buffered: Item | undefined;
  let usedWeight = 0;
  let failed: { error: unknown } | undefined;
  for (;;) {
    await control.wait(signal);
    if (failed) throw failed.error;
    while (slots.length && !control.paused) {
      buffered ??= next();
      if (buffered === undefined) break;
      const cost = Math.max(1, weight(buffered));
      if (pending.length && usedWeight + cost > maxWeight) break;
      const slot = slots.shift();
      if (slot === undefined) break;
      const item = buffered;
      buffered = undefined;
      usedWeight += cost;
      const result = Promise.resolve()
        .then(() => {
          signal.throwIfAborted();
          return run(item, slot);
        })
        .then(
          (value) => ({ value }),
          (error: unknown) => {
            failed = { error };
            return failed;
          },
        );
      pending.push({ item, slot, weight: cost, result });
    }
    const task = pending.shift();
    if (!task) return;
    const result = await task.result;
    signal.throwIfAborted();
    if ("error" in result) throw result.error;
    await commit(task.item, result.value);
    usedWeight = pending.reduce((sum, entry) => sum + entry.weight, 0);
    slots.push(task.slot);
  }
}

/** Incremental hierarchy traversal independent of camera visibility and batch size. */
export class ClashNodeQueue {
  private readonly nodes: LodTreeNode[];
  private readonly discovered = new Set<number>();
  private cursor = 0;
  completed = 0;

  /**
   * @param root Source hierarchy root; children may be discovered while loading.
   * @param relevant Conservative scope test, including clearance and fit context.
   */
  constructor(
    root: LodTreeNode,
    private readonly relevant: (node: LodTreeNode) => boolean,
  ) {
    this.nodes = [root];
    this.discovered.add(root.id);
  }

  /** @returns Remaining discovered work, excluding geometrically irrelevant nodes. */
  get pending(): number {
    return this.nodes.length - this.cursor;
  }

  /** @returns Next relevant node, with no total-job cutoff. */
  next(): LodTreeNode | undefined {
    while (this.cursor < this.nodes.length) {
      const node = this.nodes[this.cursor++];
      if (this.relevant(node)) return node;
    }
  }

  /**
   * @param node Node whose points or hierarchy have been inspected.
   * @param refine Whether finer descendants are required for the requested density.
   */
  complete(node: LodTreeNode, refine: boolean): void {
    this.completed++;
    if (refine) {
      for (const child of node.children) {
        if (!this.discovered.has(child.id) && this.relevant(child)) {
          this.discovered.add(child.id);
          this.nodes.push(child);
        }
      }
    }
  }
}

/** In-session pause/resume at resource-release boundaries. */
export class ClashJobControl {
  paused = false;
  private readonly listeners = new Set<() => void>();

  /** Request a pause after the active work item releases its resources. */
  pause(): void {
    this.paused = true;
  }

  /** Resume the same queue without restarting completed work. */
  resume(): void {
    this.paused = false;
    for (const listener of this.listeners) listener();
  }

  /** @param signal Job cancellation, independent of pausing. */
  async wait(signal: AbortSignal): Promise<void> {
    signal.throwIfAborted();
    if (!this.paused) return;
    await new Promise<void>((resolve, reject) => {
      const finish = (): void => {
        this.listeners.delete(finish);
        signal.removeEventListener("abort", abort);
        resolve();
      };
      const abort = (): void => {
        this.listeners.delete(finish);
        signal.removeEventListener("abort", abort);
        reject(signal.reason);
      };
      this.listeners.add(finish);
      signal.addEventListener("abort", abort, { once: true });
    });
  }
}
